import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { AuthProvider } from '@/lib/AuthContext'
import { AuthGate } from '@/components/auth/AuthGate'
import { SideNav } from '@/components/layout/SideNav'
import { VixBanner } from '@/components/layout/VixBanner'
import { FloatingChatButton } from '@/components/portfolio-manager/FloatingChatButton'
import { MobileNavProvider, useMobileNav } from '@/lib/mobileNav'
import AdminUsersPage from '@/pages/AdminUsersPage'
import { WatchlistPage } from '@/pages/WatchlistPage'
import { PortfolioPage } from '@/pages/PortfolioPage'
import { TradePage } from '@/pages/TradePage'
import { ProjectionsPage } from '@/pages/ProjectionsPage'
import { ImportPage } from '@/pages/ImportPage'
import RecommendationsPage from '@/pages/RecommendationsPage'
import AlertsPage from '@/pages/AlertsPage'
import ChatbotPage from '@/pages/ChatbotPage'
import ConstitutionPage from '@/pages/ConstitutionPage'
import ConstitutionEditPage from '@/pages/ConstitutionEditPage'
import TMFRankingsPage from '@/pages/TMFRankingsPage'
import PromptLibraryPage from '@/pages/PromptLibraryPage'
import AIUsagePage from '@/pages/AIUsagePage'
import ReportReaderPage from '@/pages/ReportReaderPage'
import WatchlistConfigPage from '@/pages/WatchlistConfigPage'
import MarketDataPage from '@/pages/MarketDataPage'
import { SchwabCallbackPage } from '@/pages/SchwabCallbackPage'

function MobileTopBar() {
  const { openDrawer, pageTitle, pageSubtitle } = useMobileNav()
  return (
    <div className="lg:hidden flex items-center gap-3 px-3 h-14 border-b border-[var(--color-border)] bg-[var(--color-bg-primary)] sticky top-0 z-30">
      <button
        onClick={openDrawer}
        aria-label="Open navigation menu"
        className="w-10 h-10 flex items-center justify-center rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)] text-xl"
      >
        ☰
      </button>
      <div className="min-w-0">
        <div className="text-base font-bold text-[var(--color-text-primary)] truncate">
          {pageTitle || 'Trade Manager'}
        </div>
        {pageSubtitle && (
          <div className="text-[11px] text-[var(--color-text-muted)] truncate -mt-0.5">{pageSubtitle}</div>
        )}
      </div>
    </div>
  )
}

function MobileDrawer() {
  const { drawerOpen, closeDrawer } = useMobileNav()
  if (!drawerOpen) return null
  return (
    <div className="lg:hidden fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-black/45" onClick={closeDrawer} aria-hidden="true" />
      <div className="relative h-full shadow-2xl">
        <button
          onClick={closeDrawer}
          aria-label="Close navigation menu"
          className="absolute top-2 right-2 z-10 w-9 h-9 flex items-center justify-center rounded-lg bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)]"
        >
          ✕
        </button>
        <SideNav mobile onNavigate={closeDrawer} />
      </div>
    </div>
  )
}

function Shell() {
  return (
    <div className="flex h-screen overflow-hidden">
      {/* Persistent sidebar — desktop only */}
      <div className="hidden lg:flex">
        <SideNav />
      </div>
      {/* Mobile slide-over drawer */}
      <MobileDrawer />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <MobileTopBar />
        <VixBanner />
        <main className="flex-1 overflow-y-auto">
          <Routes>
                  <Route path="/" element={<WatchlistPage />} />
                  <Route path="/schwab/callback" element={<SchwabCallbackPage />} />
                  <Route path="/watchlist-config" element={<WatchlistConfigPage />} />
                  <Route path="/market-data" element={<MarketDataPage />} />
                  <Route path="/portfolio" element={<PortfolioPage />} />
                  <Route path="/trades/:accountSlug" element={<TradePage />} />
                  <Route path="/projections" element={<ProjectionsPage />} />
                  <Route path="/import" element={<ImportPage />} />
                  <Route path="/admin/users" element={<AdminUsersPage />} />
                  <Route path="/portfolio-manager/recommendations" element={<RecommendationsPage />} />
                  <Route path="/portfolio-manager/alerts" element={<AlertsPage />} />
                  <Route path="/portfolio-manager/chat" element={<ChatbotPage />} />
                  <Route path="/portfolio-manager/constitution" element={<ConstitutionPage />} />
                  <Route path="/portfolio-manager/constitution/edit" element={<ConstitutionEditPage />} />
                  <Route path="/portfolio-manager/tmf-rankings" element={<TMFRankingsPage />} />
                  <Route path="/portfolio-manager/prompts" element={<PromptLibraryPage />} />
                  <Route path="/portfolio-manager/usage" element={<AIUsagePage />} />
                  <Route path="/report" element={<ReportReaderPage />} />
                </Routes>
              </main>
            </div>
            <FloatingChatButton />
          </div>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <AuthGate>
        <BrowserRouter>
          <MobileNavProvider>
            <Shell />
          </MobileNavProvider>
        </BrowserRouter>
      </AuthGate>
    </AuthProvider>
  )
}
